/**
 * MessagingPage.tsx
 * Real-time internal messaging with DMs and group conversations.
 * Uses WebSocket for live delivery, REST for history and file attachments.
 *
 * Place at: frontend/src/pages/MessagingPage.tsx
 */
import React, { useState, useEffect, useRef, useCallback } from "react";

// ─── Inline SVG Icons ─────────────────────────────────────────────────────────
const Icon = ({ d, size = 18, color = "currentColor", style = {} }: any) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
    <path d={d} />
  </svg>
);
const Ic = {
  MessageSquare: (p: any) => <Icon d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" {...p}/>,
  Send:          (p: any) => <Icon d="M22 2L11 13 M22 2l-7 20-4-9-9-4 20-7z" {...p}/>,
  Plus:          (p: any) => <Icon d="M12 5v14 M5 12h14" {...p}/>,
  Search:        (p: any) => <Icon d="M11 19a8 8 0 100-16 8 8 0 000 16z M21 21l-4.35-4.35" {...p}/>,
  Users:         (p: any) => <Icon d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 11a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75" {...p}/>,
  User:          (p: any) => <Icon d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 11a4 4 0 100-8 4 4 0 000 8z" {...p}/>,
  X:             (p: any) => <Icon d="M18 6L6 18 M6 6l12 12" {...p}/>,
  Paperclip:     (p: any) => <Icon d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" {...p}/>,
  Download:      (p: any) => <Icon d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3" {...p}/>,
  Reply:         (p: any) => <Icon d="M9 17H5a2 2 0 01-2-2V5a2 2 0 012-2h11a2 2 0 012 2v3 M13 21l4-4-4-4 M17 17H9" {...p}/>,
  Edit:          (p: any) => <Icon d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" {...p}/>,
  Trash:         (p: any) => <Icon d="M3 6h18 M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6 M10 11v6 M14 11v6 M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" {...p}/>,
  Check:         (p: any) => <Icon d="M20 6L9 17l-5-5" {...p}/>,
  CheckCheck:    (p: any) => <Icon d="M18 6L7 17l-5-5 M23 6L12 17" {...p}/>,
  File:          (p: any) => <Icon d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6" {...p}/>,
  Image:         (p: any) => <Icon d="M21 19a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h4l2 3h8a2 2 0 012 2z" {...p}/>,
  Alert:         (p: any) => <Icon d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01" {...p}/>,
  ChevronDown:   (p: any) => <Icon d="M6 9l6 6 6-6" {...p}/>,
  MoreVertical:  (p: any) => <Icon d="M12 13a1 1 0 100-2 1 1 0 000 2z M12 6a1 1 0 100-2 1 1 0 000 2z M12 20a1 1 0 100-2 1 1 0 000 2z" {...p}/>,
};

// ─── Theme ────────────────────────────────────────────────────────────────────
const darkTheme = {
  bg: "#0B1120", surface: "#111827", surfaceHover: "#1a2332",
  border: "#1E293B", borderLight: "#334155",
  text: "#E2E8F0", textMuted: "#94A3B8", textDim: "#64748B",
  accent: "#3B82F6", accentGlow: "rgba(59,130,246,0.15)",
  green: "#10B981", yellow: "#F59E0B", red: "#EF4444",
  purple: "#8B5CF6", orange: "#F97316",
  bubble: { mine: "#1D4ED8", theirs: "#1E293B" },
};
const lightTheme = {
  bg: "#F1F5F9", surface: "#FFFFFF", surfaceHover: "#F8FAFC",
  border: "#E2E8F0", borderLight: "#CBD5E1",
  text: "#1E293B", textMuted: "#475569", textDim: "#94A3B8",
  accent: "#3B82F6", accentGlow: "rgba(59,130,246,0.08)",
  green: "#10B981", yellow: "#F59E0B", red: "#EF4444",
  purple: "#8B5CF6", orange: "#F97316",
  bubble: { mine: "#3B82F6", theirs: "#E2E8F0" },
};
const getTheme = () => {
  const s = typeof localStorage !== "undefined" ? localStorage.getItem("engportal-theme") : null;
  return s === "light" ? lightTheme : darkTheme;
};

// ─── Types ────────────────────────────────────────────────────────────────────
interface Participant { id: number; name: string; initials: string; is_admin: boolean; last_read_at?: string; }
interface LastMessage { id: number; content: string; sender_name: string; created_at: string; deleted: boolean; }
interface Conversation {
  id: number; type: string; name: string; display_name: string;
  context_label: string; last_message?: LastMessage; unread_count: number;
  participants_detail: Participant[]; created_at: string; updated_at: string;
}
interface Message {
  id: number; conversation_id: number; content: string;
  sender_id: number; sender_name: string; sender_initials: string;
  reply_to?: { id: number; content: string; sender_name: string };
  has_attachment: boolean; attachment_name?: string; attachment_url?: string;
  attachment_mime?: string;
  edited: boolean; deleted: boolean; created_at: string;
  pending?: boolean;
}
interface TypingUser { user_id: number; user_name: string; }

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getCookie(name: string): string {
  let val = "";
  if (document.cookie) {
    document.cookie.split(";").forEach(c => {
      const t = c.trim();
      if (t.startsWith(name + "=")) val = decodeURIComponent(t.slice(name.length + 1));
    });
  }
  return val;
}
async function api(url: string, opts: RequestInit = {}) {
  const { headers: extra, ...rest } = opts as any;
  const res = await fetch(url, {
    headers: { "X-CSRFToken": getCookie("csrftoken"), ...(extra || {}) },
    credentials: "include", ...rest,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const d = await res.json(); msg = d.error || d.detail || JSON.stringify(d); } catch {}
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  return res.json();
}
function fmtTime(s: string): string {
  const d = new Date(s);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 86400000 && d.getDate() === now.getDate())
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (diff < 604800000)
    return d.toLocaleDateString("en-US", { weekday: "short" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function fmtBytes(b: number): string {
  if (!b) return "";
  if (b < 1024) return `${b}B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / 1048576).toFixed(1)}MB`;
}
function Avatar({ name, size = 32, color = "#3B82F6" }: { name: string; size?: number; color?: string }) {
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: color + "33", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <span style={{ color, fontSize: size * 0.38, fontWeight: 700 }}>{(name || "?").slice(0, 2).toUpperCase()}</span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MessagingPage({ currentUser, onUnreadChange }: { currentUser?: any; onUnreadChange?: (count: number) => void }) {
  const theme = getTheme();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [input, setInput] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<Set<number>>(new Set());
  const [showNewConv, setShowNewConv] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const presenceWsRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const typingTimerRef = useRef<any>(null);

  const isManager = currentUser?.role === 'engineering_manager' || currentUser?.role === 'executive';

  // ── Load conversations ──────────────────────────────────────────────────────
  useEffect(() => {
    loadConversations();
    connectPresence();
    return () => { presenceWsRef.current?.close(); };
  }, []);

  async function loadConversations() {
    setLoading(true);
    try {
      const data = await api("/api/messaging/conversations/");
      setConversations(Array.isArray(data) ? data : data?.results || []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  // ── Presence WebSocket ──────────────────────────────────────────────────────
  function connectPresence() {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${window.location.host}/ws/presence/`);
    presenceWsRef.current = ws;
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === "presence") {
        setOnlineUsers(prev => {
          const next = new Set(prev);
          if (data.status === "online") next.add(data.user_id);
          else next.delete(data.user_id);
          return next;
        });
      }
      if (data.type === "new_message_notification") {
        // Refresh conversation list to update unread counts
        loadConversations();
      }
    };
    ws.onclose = () => setTimeout(connectPresence, 3000);
  }

  // ── Select conversation ─────────────────────────────────────────────────────
  async function selectConversation(conv: Conversation) {
    setSelected(conv);
    setMessages([]);
    setReplyTo(null);
    setInput("");

    // Disconnect old WebSocket
    wsRef.current?.close();
    connectChat(conv.id);
    await loadMessages(conv.id);
  }

  function connectChat(convId: number) {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${window.location.host}/ws/chat/${convId}/`);
    wsRef.current = ws;

    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === "message") {
        const msg: Message = data.message;
        setMessages(prev => {
          // Avoid duplicates (our own optimistic message)
          if (prev.find(m => m.id === msg.id)) return prev;
          return [...prev.filter(m => !m.pending || m.sender_id !== msg.sender_id), msg];
        });
        scrollToBottom();
        // Mark read
        ws.send(JSON.stringify({ type: "read" }));
        // Update conversation last message
        setConversations(prev => prev.map(c => c.id === convId ? {
          ...c, last_message: { id: msg.id, content: msg.content, sender_name: msg.sender_name, created_at: msg.created_at, deleted: false }, unread_count: 0, updated_at: msg.created_at
        } : c).sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()));
      }
      if (data.type === "typing") {
        const { user_id, user_name, is_typing } = data;
        setTypingUsers(prev => is_typing
          ? [...prev.filter(u => u.user_id !== user_id), { user_id, user_name }]
          : prev.filter(u => u.user_id !== user_id)
        );
      }
      if (data.type === "read") {
        // Could highlight read receipts here
      }
    };
    ws.onclose = () => {
      // Reconnect if still viewing this conversation
      if (selected?.id === convId) setTimeout(() => connectChat(convId), 2000);
    };
  }

  async function loadMessages(convId: number, beforeId?: number) {
    setMessagesLoading(true);
    try {
      const params = beforeId ? `?before=${beforeId}&limit=50` : "?limit=50";
      const data = await api(`/api/messaging/conversations/${convId}/messages/${params}`);
      if (beforeId) {
        setMessages(prev => [...(data.messages || []), ...prev]);
      } else {
        setMessages(data.messages || []);
        setTimeout(scrollToBottom, 50);
      }
      setHasMore(data.has_more || false);
    } catch (e: any) { setError(e.message); }
    finally { setMessagesLoading(false); }
  }

  function scrollToBottom() {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  // ── Send message ────────────────────────────────────────────────────────────
  async function sendMessage() {
    if ((!input.trim() && !attachment) || !selected || sending) return;

    if (attachment) {
      // File attachments go via REST
      setSending(true);
      try {
        const fd = new FormData();
        fd.append("content", input.trim());
        fd.append("attachment", attachment);
        if (replyTo) fd.append("reply_to", String(replyTo.id));
        const msg = await api(`/api/messaging/conversations/${selected.id}/send/`, { method: "POST", body: fd });
        setMessages(prev => [...prev, msg]);
        scrollToBottom();
        setInput(""); setAttachment(null); setReplyTo(null);
      } catch (e: any) { setError(e.message); }
      finally { setSending(false); }
    } else {
      // Text messages go via WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "message",
          content: input.trim(),
          reply_to: replyTo?.id || null,
        }));
        setInput(""); setReplyTo(null);
        inputRef.current?.focus();
      } else {
        setError("Connection lost. Reconnecting…");
      }
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
      return;
    }
    // Typing indicator
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "typing", is_typing: true }));
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => {
        wsRef.current?.send(JSON.stringify({ type: "typing", is_typing: false }));
      }, 1500);
    }
  }

  const filtered = conversations.filter(c =>
    !search || c.display_name.toLowerCase().includes(search.toLowerCase())
  );
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
  React.useEffect(() => { onUnreadChange?.(totalUnread); }, [totalUnread]);

  // ── Group messages by date ─────────────────────────────────────────────────
  function groupByDate(msgs: Message[]) {
    const groups: { date: string; messages: Message[] }[] = [];
    msgs.forEach(msg => {
      const d = new Date(msg.created_at).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
      const last = groups[groups.length - 1];
      if (last && last.date === d) last.messages.push(msg);
      else groups.push({ date: d, messages: [msg] });
    });
    return groups;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: theme.bg }}>
      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
        <Ic.MessageSquare size={20} color={theme.accent} />
        <h2 style={{ margin: 0, color: theme.text, fontSize: 18, fontWeight: 600 }}>Messages</h2>
        {totalUnread > 0 && (
          <span style={{ background: theme.red, color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 10 }}>{totalUnread}</span>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowNewConv(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: theme.accent, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          <Ic.Plus size={14} /> New Message
        </button>
      </div>

      {error && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #EF4444", borderRadius: 8, margin: "8px 16px", padding: "9px 12px", color: "#EF4444", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <Ic.Alert size={13} /> {error}
          <button onClick={() => setError("")} style={{ marginLeft: "auto", background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
      )}

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* ── Conversation list ── */}
        <div style={{ width: 280, borderRight: `1px solid ${theme.border}`, display: "flex", flexDirection: "column", flexShrink: 0 }}>
          <div style={{ padding: "10px 12px 8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "7px 10px" }}>
              <Ic.Search size={13} color={theme.textDim} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search conversations…"
                style={{ background: "none", border: "none", color: theme.text, fontSize: 13, outline: "none", flex: 1 }} />
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading ? (
              <div style={{ padding: 20, color: theme.textDim, fontSize: 13, textAlign: "center" }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 20, color: theme.textDim, fontSize: 13, textAlign: "center" }}>
                {search ? "No conversations found" : "No messages yet — start a new conversation"}
              </div>
            ) : filtered.map(conv => {
              const isActive = selected?.id === conv.id;
              const otherParticipant = conv.type === 'direct'
                ? conv.participants_detail.find(p => p.id !== currentUser?.id)
                : null;
              const isOnline = otherParticipant ? onlineUsers.has(otherParticipant.id) : false;

              return (
                <div key={conv.id} onClick={() => selectConversation(conv)}
                  style={{ padding: "10px 14px", cursor: "pointer", borderLeft: `3px solid ${isActive ? theme.accent : "transparent"}`, background: isActive ? theme.accentGlow : "transparent", transition: "all 0.12s" }}
                  onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = theme.surfaceHover; }}
                  onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      {conv.type === 'direct' ? (
                        <Avatar name={conv.display_name} size={36} color={theme.accent} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: theme.purple + "33", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Ic.Users size={16} color={theme.purple} />
                        </div>
                      )}
                      {isOnline && (
                        <div style={{ position: "absolute", bottom: 1, right: 1, width: 9, height: 9, borderRadius: "50%", background: theme.green, border: `2px solid ${theme.surface}` }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ color: isActive ? theme.accent : theme.text, fontSize: 13, fontWeight: conv.unread_count > 0 ? 700 : 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 140 }}>{conv.display_name}</span>
                        {conv.last_message && <span style={{ color: theme.textDim, fontSize: 10, flexShrink: 0 }}>{fmtTime(conv.last_message.created_at)}</span>}
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2 }}>
                        <span style={{ color: conv.unread_count > 0 ? theme.textMuted : theme.textDim, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160, fontWeight: conv.unread_count > 0 ? 500 : 400 }}>
                          {conv.last_message ? (conv.last_message.deleted ? "Message deleted" : conv.last_message.content) : "No messages yet"}
                        </span>
                        {conv.unread_count > 0 && (
                          <span style={{ background: theme.accent, color: "#fff", fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10, flexShrink: 0 }}>{conv.unread_count}</span>
                        )}
                      </div>
                      {conv.context_label && (
                        <div style={{ color: theme.textDim, fontSize: 10, marginTop: 1 }}>📎 {conv.context_label}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Chat panel ── */}
        {!selected ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
            <Ic.MessageSquare size={48} color={theme.textDim} />
            <div style={{ color: theme.textMuted, fontSize: 14 }}>Select a conversation or start a new one</div>
            <button onClick={() => setShowNewConv(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: theme.accent, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              <Ic.Plus size={14} /> New Message
            </button>
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

            {/* Chat header */}
            <div style={{ padding: "12px 20px", borderBottom: `1px solid ${theme.border}`, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              {selected.type === 'direct' ? (
                <div style={{ position: "relative" }}>
                  <Avatar name={selected.display_name} size={36} color={theme.accent} />
                  {onlineUsers.has(selected.participants_detail.find(p => p.id !== currentUser?.id)?.id || -1) && (
                    <div style={{ position: "absolute", bottom: 1, right: 1, width: 9, height: 9, borderRadius: "50%", background: theme.green, border: `2px solid ${theme.surface}` }} />
                  )}
                </div>
              ) : (
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: theme.purple + "33", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Ic.Users size={18} color={theme.purple} />
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}>{selected.display_name}</div>
                <div style={{ color: theme.textDim, fontSize: 11 }}>
                  {selected.type === 'direct'
                    ? (onlineUsers.has(selected.participants_detail.find(p => p.id !== currentUser?.id)?.id || -1) ? "🟢 Online" : "Offline")
                    : `${selected.participants_detail.length} members`
                  }
                  {selected.context_label && ` · 📎 ${selected.context_label}`}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 2 }}>
              {hasMore && (
                <button onClick={() => loadMessages(selected.id, messages[0]?.id)} disabled={messagesLoading}
                  style={{ alignSelf: "center", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textMuted, fontSize: 12, padding: "6px 14px", cursor: "pointer", marginBottom: 8 }}>
                  {messagesLoading ? "Loading…" : "Load older messages"}
                </button>
              )}

              {groupByDate(messages).map(group => (
                <React.Fragment key={group.date}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "12px 0 8px" }}>
                    <div style={{ flex: 1, height: 1, background: theme.border }} />
                    <span style={{ color: theme.textDim, fontSize: 11, fontWeight: 500, whiteSpace: "nowrap" }}>{group.date}</span>
                    <div style={{ flex: 1, height: 1, background: theme.border }} />
                  </div>
                  {group.messages.map((msg, i) => {
                    const isMine = msg.sender_id === currentUser?.id;
                    const prevMsg = group.messages[i - 1];
                    const showAvatar = !isMine && (!prevMsg || prevMsg.sender_id !== msg.sender_id);
                    const showName = !isMine && showAvatar;

                    return (
                      <MessageBubble
                        key={msg.id} msg={msg} isMine={isMine}
                        showAvatar={showAvatar} showName={showName}
                        theme={theme} currentUser={currentUser}
                        onReply={() => setReplyTo(msg)}
                      />
                    );
                  })}
                </React.Fragment>
              ))}

              {typingUsers.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", color: theme.textDim, fontSize: 12 }}>
                  <div style={{ display: "flex", gap: 3 }}>
                    {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: theme.textDim, animation: `bounce 1s ${i * 0.2}s infinite` }} />)}
                  </div>
                  {typingUsers.map(u => u.user_name).join(", ")} {typingUsers.length === 1 ? "is" : "are"} typing…
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply banner */}
            {replyTo && (
              <div style={{ margin: "0 20px", padding: "8px 12px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "8px 8px 0 0", borderBottom: "none", display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 3, height: 32, background: theme.accent, borderRadius: 2, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: theme.accent, fontSize: 11, fontWeight: 600 }}>Replying to {replyTo.sender_name}</div>
                  <div style={{ color: theme.textMuted, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{replyTo.content}</div>
                </div>
                <button onClick={() => setReplyTo(null)} style={{ background: "none", border: "none", color: theme.textDim, cursor: "pointer", padding: 4 }}><Ic.X size={14} /></button>
              </div>
            )}

            {/* Attachment preview */}
            {attachment && (
              <div style={{ margin: "0 20px", padding: "8px 12px", background: theme.surface, border: `1px solid ${theme.border}`, borderTop: "none", borderBottom: "none", display: "flex", alignItems: "center", gap: 8 }}>
                <Ic.Paperclip size={14} color={theme.accent} />
                <span style={{ color: theme.text, fontSize: 12, flex: 1 }}>{attachment.name}</span>
                <button onClick={() => setAttachment(null)} style={{ background: "none", border: "none", color: theme.textDim, cursor: "pointer", padding: 4 }}><Ic.X size={14} /></button>
              </div>
            )}

            {/* Input area */}
            <div style={{ padding: "12px 20px", borderTop: `1px solid ${theme.border}`, flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8, background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: replyTo || attachment ? "0 0 12px 12px" : 12, padding: "8px 12px", margin: replyTo || attachment ? "0 0 0 0" : 0 }}>
                <input ref={fileRef} type="file" style={{ display: "none" }} onChange={e => setAttachment(e.target.files?.[0] || null)} />
                <button onClick={() => fileRef.current?.click()} title="Attach file"
                  style={{ background: "none", border: "none", color: theme.textDim, cursor: "pointer", padding: "4px", flexShrink: 0 }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = theme.accent}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = theme.textDim}
                >
                  <Ic.Paperclip size={17} />
                </button>
                <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
                  rows={1}
                  style={{ flex: 1, background: "none", border: "none", color: theme.text, fontSize: 14, outline: "none", resize: "none", maxHeight: 120, overflowY: "auto", lineHeight: 1.5, padding: "4px 0" }}
                />
                <button onClick={sendMessage} disabled={(!input.trim() && !attachment) || sending}
                  style={{ background: theme.accent, border: "none", borderRadius: 8, color: "#fff", cursor: (!input.trim() && !attachment) || sending ? "not-allowed" : "pointer", padding: "8px 10px", opacity: (!input.trim() && !attachment) || sending ? 0.5 : 1, flexShrink: 0 }}>
                  <Ic.Send size={16} />
                </button>
              </div>
              <div style={{ color: theme.textDim, fontSize: 10, marginTop: 4, paddingLeft: 4 }}>Enter to send · Shift+Enter for new line</div>
            </div>
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewConv && (
        <NewConversationModal theme={theme} currentUser={currentUser}
          onClose={() => setShowNewConv(false)}
          onSuccess={(conv: Conversation) => {
            setConversations(prev => [conv, ...prev.filter(c => c.id !== conv.id)]);
            setShowNewConv(false);
            selectConversation(conv);
          }}
        />
      )}

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}

// ─── Message Bubble ───────────────────────────────────────────────────────────
function MessageBubble({ msg, isMine, showAvatar, showName, theme, currentUser, onReply }: any) {
  const [showActions, setShowActions] = useState(false);

  if (msg.deleted) {
    return (
      <div style={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", marginBottom: 2 }}>
        <span style={{ color: theme.textDim, fontSize: 12, fontStyle: "italic", padding: "4px 0" }}>Message deleted</span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 8, marginBottom: showName ? 6 : 2 }}
      onMouseEnter={() => setShowActions(true)} onMouseLeave={() => setShowActions(false)}
    >
      {!isMine && (
        <div style={{ width: 28, flexShrink: 0, marginBottom: 2 }}>
          {showAvatar && <Avatar name={msg.sender_name} size={28} color="#8B5CF6" />}
        </div>
      )}

      <div style={{ maxWidth: "65%", display: "flex", flexDirection: "column", alignItems: isMine ? "flex-end" : "flex-start" }}>
        {showName && <div style={{ color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 3, paddingLeft: 4 }}>{msg.sender_name}</div>}

        {/* Reply preview */}
        {msg.reply_to && (
          <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: "6px 6px 0 0", padding: "4px 10px", marginBottom: -4, maxWidth: "100%", opacity: 0.8 }}>
            <div style={{ color: theme.accent, fontSize: 10, fontWeight: 600 }}>{msg.reply_to.sender_name}</div>
            <div style={{ color: theme.textDim, fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{msg.reply_to.content}</div>
          </div>
        )}

        <div style={{
          background: isMine ? theme.bubble.mine : theme.bubble.theirs,
          color: isMine ? "#fff" : theme.text,
          borderRadius: isMine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
          padding: "8px 12px",
          fontSize: 14, lineHeight: 1.5,
          wordBreak: "break-word" as const,
          opacity: msg.pending ? 0.6 : 1,
        }}>
          {msg.content}
          {msg.has_attachment && (
            <div style={{ marginTop: 6, padding: "6px 10px", background: "rgba(0,0,0,0.15)", borderRadius: 6, display: "flex", alignItems: "center", gap: 6 }}>
              <Ic.Paperclip size={13} />
              <span style={{ fontSize: 12 }}>{msg.attachment_name}</span>
              {msg.attachment_url && (
                <a href={msg.attachment_url} download style={{ marginLeft: "auto", color: "inherit", opacity: 0.8 }}>
                  <Ic.Download size={13} />
                </a>
              )}
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2, paddingLeft: 4, paddingRight: 4 }}>
          <span style={{ color: theme.textDim, fontSize: 10 }}>{fmtTime(msg.created_at)}</span>
          {msg.edited && <span style={{ color: theme.textDim, fontSize: 10 }}>(edited)</span>}
          {msg.pending && <span style={{ color: theme.textDim, fontSize: 10 }}>Sending…</span>}
        </div>
      </div>

      {/* Action buttons on hover */}
      {showActions && !msg.pending && (
        <div style={{ display: "flex", gap: 4, background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "4px 6px", flexShrink: 0 }}>
          <ActionBtn icon={<Ic.Reply size={13} />} title="Reply" onClick={onReply} theme={theme} />
        </div>
      )}
    </div>
  );
}

function ActionBtn({ icon, title, onClick, theme }: any) {
  return (
    <button title={title} onClick={onClick}
      style={{ background: "none", border: "none", color: theme.textMuted, cursor: "pointer", padding: 3, borderRadius: 4, display: "flex" }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = theme.accent; (e.currentTarget as HTMLElement).style.background = theme.accentGlow; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = theme.textMuted; (e.currentTarget as HTMLElement).style.background = "none"; }}
    >{icon}</button>
  );
}

// ─── New Conversation Modal ───────────────────────────────────────────────────
function NewConversationModal({ theme, currentUser, onClose, onSuccess }: any) {
  const [users, setUsers] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [groupName, setGroupName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api("/api/quality-docs/users-with-tags/")
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = users.filter(u =>
    u.id !== currentUser?.id &&
    (!search || (u.name || u.username || "").toLowerCase().includes(search.toLowerCase()))
  );
  const isGroup = selected.length > 1;

  async function start() {
    if (selected.length === 0) { setErr("Select at least one person"); return; }
    setBusy(true); setErr("");
    try {
      const conv = await api("/api/messaging/conversations/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: isGroup ? "group" : "direct",
          name: isGroup ? groupName : "",
          participant_ids: selected.map(u => u.id),
        }),
      });
      onSuccess(conv);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const is: React.CSSProperties = { width: "100%", padding: "9px 12px", background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 13, outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 24, maxWidth: 440, width: "95%", maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <Ic.MessageSquare size={17} color={theme.accent} />
          <span style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}>New Message</span>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: theme.textDim, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {err && <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #EF4444", borderRadius: 8, padding: "9px 12px", color: "#EF4444", fontSize: 13, marginBottom: 12 }}>{err}</div>}

        {/* Selected chips */}
        {selected.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {selected.map(u => (
              <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 5, background: theme.accent + "22", border: `1px solid ${theme.accent}`, borderRadius: 20, padding: "3px 10px 3px 8px", fontSize: 12, color: theme.accent }}>
                {u.name || u.username}
                <button onClick={() => setSelected(p => p.filter(x => x.id !== u.id))} style={{ background: "none", border: "none", color: theme.accent, cursor: "pointer", padding: 0, marginLeft: 2, fontSize: 14, lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
        )}

        {isGroup && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>Group Name</label>
            <input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="e.g. FPD-TAR Team" style={is} />
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 8, background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "7px 10px", marginBottom: 10 }}>
          <Ic.Search size={13} color={theme.textDim} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search people…"
            style={{ background: "none", border: "none", color: theme.text, fontSize: 13, outline: "none", flex: 1 }} />
        </div>

        <div style={{ flex: 1, overflowY: "auto", marginBottom: 16 }}>
          {loading ? (
            <div style={{ color: theme.textDim, fontSize: 13, textAlign: "center", padding: 16 }}>Loading…</div>
          ) : filtered.map(u => {
            const isSelected = selected.some(s => s.id === u.id);
            return (
              <div key={u.id} onClick={() => setSelected(p => isSelected ? p.filter(x => x.id !== u.id) : [...p, u])}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: isSelected ? theme.accent + "15" : "transparent", marginBottom: 2 }}
                onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = theme.surfaceHover; }}
                onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <Avatar name={u.name || u.username} size={32} color={theme.accent} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: theme.text, fontSize: 13, fontWeight: 500 }}>{u.name || u.username}</div>
                  {u.role && <div style={{ color: theme.textDim, fontSize: 11 }}>{u.role.replace(/_/g, " ")}</div>}
                </div>
                {isSelected && <Ic.Check size={16} color={theme.accent} />}
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 18px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textMuted, cursor: "pointer", fontSize: 13 }}>Cancel</button>
          <button onClick={start} disabled={selected.length === 0 || busy || (isGroup && !groupName.trim())}
            style={{ padding: "9px 18px", background: theme.accent, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, opacity: selected.length === 0 || busy ? 0.6 : 1 }}>
            {busy ? "Starting…" : isGroup ? "Create Group" : "Start Chat"}
          </button>
        </div>
      </div>
    </div>
  );
}
